/**
 * Sales Reconciliation Tab
 * View daily sales data, payment breakdowns, and M-PAiSA reference codes
 * Clean up stale orders with one click
 */
import { useState, useEffect, useCallback } from 'react';
import { 
  Calendar, DollarSign, CreditCard, Smartphone, Banknote, RefreshCw, 
  Trash2, CheckCircle, AlertCircle, ChevronLeft, ChevronRight, Download,
  Receipt, TrendingUp, Clock, Users, QrCode
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { api } from '@/utils/apiClient';

export default function SalesReconciliationTab() {
  const [data, setData] = useState(null);
  const [monthlySummary, setMonthlySummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cleaningUp, setCleaningUp] = useState(false);
  const [selectedDate, setSelectedDate] = useState(() => {
    // Default to today in Fiji time (UTC+12)
    const now = new Date();
    const fijiOffset = 12 * 60; // Fiji is UTC+12
    const localOffset = now.getTimezoneOffset();
    const fijiTime = new Date(now.getTime() + (fijiOffset + localOffset) * 60000);
    return fijiTime.toISOString().split('T')[0];
  });

  const fetchReconciliation = useCallback(async () => {
    setLoading(true);
    try {
      const [dailyRes, monthlyRes] = await Promise.all([
        api.get(`/admin/analytics/sales-reconciliation?date=${selectedDate}`),
        api.get('/admin/analytics/monthly-sales-summary')
      ]);
      setData(dailyRes.data);
      setMonthlySummary(monthlyRes.data);
    } catch (err) {
      console.error('Error fetching reconciliation:', err);
      toast.error('Failed to load reconciliation data');
    }
    setLoading(false);
  }, [selectedDate]);

  useEffect(() => {
    fetchReconciliation();
  }, [fetchReconciliation]);

  const handleCleanup = async () => {
    setCleaningUp(true);
    try {
      const res = await api.post('/admin/analytics/cleanup-stale-orders');
      
      if (res.data.total_fixed > 0 || res.data.total_cancelled > 0) {
        toast.success(res.data.message, { duration: 5000 });
      } else {
        toast.info('All orders are already clean - nothing to fix');
      }
      
      // Refresh the data
      fetchReconciliation();
    } catch (err) {
      toast.error('Failed to cleanup orders');
    }
    setCleaningUp(false);
  };

  const changeDate = (days) => {
    const current = new Date(selectedDate);
    current.setDate(current.getDate() + days);
    setSelectedDate(current.toISOString().split('T')[0]);
  };

  const goToToday = () => {
    const now = new Date();
    const fijiOffset = 12 * 60;
    const localOffset = now.getTimezoneOffset();
    const fijiTime = new Date(now.getTime() + (fijiOffset + localOffset) * 60000);
    setSelectedDate(fijiTime.toISOString().split('T')[0]);
  };

  const getPaymentIcon = (method) => {
    switch (method) {
      case 'cash': return <Banknote className="w-5 h-5 text-green-600" />;
      case 'eftpos': return <CreditCard className="w-5 h-5 text-blue-600" />;
      case 'mpaisa': return <Smartphone className="w-5 h-5 text-red-600" />;
      default: return <DollarSign className="w-5 h-5 text-gray-600" />;
    }
  };

  const getPaymentLabel = (method) => {
    switch (method) {
      case 'cash': return 'Cash';
      case 'eftpos': return 'EFTPOS';
      case 'mpaisa': return 'M-PAiSA';
      default: return method;
    }
  };

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="sales-reconciliation-tab">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl text-foreground">Sales Reconciliation</h1>
          <p className="text-muted-foreground text-sm mt-1">Daily sales breakdown with M-PAiSA reference codes</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={handleCleanup}
            disabled={cleaningUp}
            className="text-red-600 border-red-200 hover:bg-red-50 dark:border-red-800 dark:hover:bg-red-950"
            data-testid="cleanup-orders-btn"
          >
            {cleaningUp ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
            Cleanup Stale Orders
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={fetchReconciliation}
            disabled={loading}
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Date Selector */}
      <div className="bg-card rounded-xl p-4 border border-border flex flex-col sm:flex-row items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={() => changeDate(-1)}>
            <ChevronLeft className="w-4 h-4" />
          </Button>
          <div className="flex items-center gap-2 px-4 py-2 bg-background rounded-lg border border-border min-w-[200px] justify-center">
            <Calendar className="w-4 h-4 text-muted-foreground" />
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="bg-transparent border-none outline-none text-foreground font-medium"
            />
          </div>
          <Button variant="outline" size="icon" onClick={() => changeDate(1)}>
            <ChevronRight className="w-4 h-4" />
          </Button>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" onClick={goToToday}>
            Today
          </Button>
          <span className="text-muted-foreground text-sm hidden sm:inline">
            {data?.date_fiji}
          </span>
        </div>
      </div>

      {/* Monthly & All-Time Summary */}
      {monthlySummary && (
        <div className="bg-card rounded-xl p-6 border border-border">
          <h2 className="font-heading text-lg text-foreground mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-primary" />
            Sales Summary
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Current Month */}
            <div className="bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl p-5 text-white">
              <div className="flex items-center justify-between mb-3">
                <span className="text-blue-100 text-sm font-medium">{monthlySummary.current_month?.label}</span>
                <span className="text-blue-100 text-xs">(Current Month)</span>
              </div>
              <p className="text-3xl font-bold">${monthlySummary.current_month?.gross_collected?.toFixed(2)}</p>
              <div className="mt-2 text-xs text-blue-100 space-y-0.5">
                <p>Net: ${monthlySummary.current_month?.net_revenue?.toFixed(2)}</p>
                <p>VAT: ${monthlySummary.current_month?.vat_collected?.toFixed(2)}</p>
                <p>{monthlySummary.current_month?.transaction_count} transactions</p>
              </div>
            </div>
            
            {/* Previous Month */}
            <div className="bg-card rounded-xl p-5 border border-border">
              <div className="flex items-center justify-between mb-3">
                <span className="text-muted-foreground text-sm font-medium">{monthlySummary.previous_month?.label}</span>
                <span className="text-muted-foreground text-xs">(Last Month)</span>
              </div>
              <p className="text-2xl font-bold text-foreground">${monthlySummary.previous_month?.gross_collected?.toFixed(2)}</p>
              <div className="mt-2 text-xs text-muted-foreground space-y-0.5">
                <p>Net: ${monthlySummary.previous_month?.net_revenue?.toFixed(2)}</p>
                <p>VAT: ${monthlySummary.previous_month?.vat_collected?.toFixed(2)}</p>
                <p>{monthlySummary.previous_month?.transaction_count} transactions</p>
              </div>
            </div>
            
            {/* All Time */}
            <div className="bg-gradient-to-br from-amber-500 to-orange-600 rounded-xl p-5 text-white">
              <div className="flex items-center justify-between mb-3">
                <span className="text-amber-100 text-sm font-medium">All Time</span>
                <span className="text-amber-100 text-xs">(Lifetime)</span>
              </div>
              <p className="text-3xl font-bold">${monthlySummary.all_time?.gross_collected?.toFixed(2)}</p>
              <div className="mt-2 text-xs text-amber-100 space-y-0.5">
                <p>Net: ${monthlySummary.all_time?.net_revenue?.toFixed(2)}</p>
                <p>VAT: ${monthlySummary.all_time?.vat_collected?.toFixed(2)}</p>
                <p>{monthlySummary.all_time?.transaction_count} transactions</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {data && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {/* NET Revenue - Primary Card */}
            <div className="bg-gradient-to-br from-green-500 to-emerald-600 rounded-xl p-5 text-white col-span-2 lg:col-span-1">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-white/20 flex items-center justify-center">
                  <DollarSign className="w-5 h-5" />
                </div>
                <span className="text-green-100 text-sm font-medium">Net Revenue</span>
              </div>
              <p className="text-3xl font-bold">${data.summary.net_revenue.toFixed(2)}</p>
              <p className="text-xs text-green-100 mt-1">What the cafe keeps</p>
            </div>

            <div className="bg-card rounded-xl p-5 border border-border">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                </div>
                <span className="text-muted-foreground text-sm">Collected</span>
              </div>
              <p className="text-2xl font-bold text-foreground">${data.summary.gross_revenue.toFixed(2)}</p>
              <p className="text-xs text-muted-foreground mt-1">Total received</p>
            </div>

            <div className="bg-card rounded-xl p-5 border border-border">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center">
                  <Receipt className="w-5 h-5 text-purple-600" />
                </div>
                <span className="text-muted-foreground text-sm">VAT (12.5%)</span>
              </div>
              <p className="text-2xl font-bold text-foreground">${data.summary.vat_collected.toFixed(2)}</p>
              <p className="text-xs text-muted-foreground mt-1">Owed to FRCS</p>
            </div>

            <div className="bg-card rounded-xl p-5 border border-border">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 rounded-lg bg-amber-100 dark:bg-amber-900/30 flex items-center justify-center">
                  <Users className="w-5 h-5 text-amber-600" />
                </div>
                <span className="text-muted-foreground text-sm">Transactions</span>
              </div>
              <p className="text-2xl font-bold text-foreground">{data.summary.total_transactions}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {data.summary.paid_orders} orders + {data.summary.coffee_pass_sales} passes
              </p>
            </div>
          </div>

          {/* Alerts for pending/cancelled */}
          {(data.summary.pending_orders > 0 || data.summary.cancelled_orders > 0) && (
            <div className="flex flex-wrap gap-3">
              {data.summary.pending_orders > 0 && (
                <div className="flex items-center gap-2 px-4 py-2 bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg">
                  <Clock className="w-4 h-4 text-yellow-600" />
                  <span className="text-yellow-800 dark:text-yellow-200 text-sm font-medium">
                    {data.summary.pending_orders} pending order(s) - use Cleanup button
                  </span>
                </div>
              )}
              {data.summary.cancelled_orders > 0 && (
                <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <AlertCircle className="w-4 h-4 text-gray-500" />
                  <span className="text-gray-600 dark:text-gray-300 text-sm">
                    {data.summary.cancelled_orders} cancelled order(s)
                  </span>
                </div>
              )}
            </div>
          )}

          {/* Payment Method Breakdown */}
          <div className="bg-card rounded-xl p-6 border border-border">
            <h2 className="font-heading text-lg text-foreground mb-4">Payment Method Breakdown</h2>
            {Object.keys(data.payment_breakdown).length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No sales recorded for this day</p>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {Object.entries(data.payment_breakdown).map(([method, info]) => (
                  <div 
                    key={method} 
                    className="flex items-center gap-3 p-4 bg-background rounded-lg border border-border"
                  >
                    {getPaymentIcon(method)}
                    <div>
                      <p className="font-medium text-foreground">{getPaymentLabel(method)}</p>
                      <p className="text-sm text-muted-foreground">{info.count} sale(s)</p>
                    </div>
                    <p className="ml-auto font-bold text-foreground">${info.total.toFixed(2)}</p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* M-PAiSA Transactions (with reference codes) */}
          {data.mpaisa_transactions && data.mpaisa_transactions.length > 0 && (
            <div className="bg-card rounded-xl p-6 border border-red-200 dark:border-red-800">
              <div className="flex items-center gap-2 mb-4">
                <Smartphone className="w-5 h-5 text-red-600" />
                <h2 className="font-heading text-lg text-foreground">M-PAiSA Transactions</h2>
                <span className="ml-auto text-sm text-muted-foreground">
                  Reference codes for reconciliation
                </span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-2 text-muted-foreground font-medium">Order #</th>
                      <th className="text-left py-3 px-2 text-muted-foreground font-medium">Amount</th>
                      <th className="text-left py-3 px-2 text-muted-foreground font-medium">M-PAiSA Ref Code</th>
                      <th className="text-left py-3 px-2 text-muted-foreground font-medium">Customer</th>
                      <th className="text-left py-3 px-2 text-muted-foreground font-medium">Items</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.mpaisa_transactions.map((txn, idx) => (
                      <tr key={idx} className="border-b border-border/50 hover:bg-muted/50">
                        <td className="py-3 px-2 font-medium">#{txn.order_number}</td>
                        <td className="py-3 px-2 font-bold text-green-600 dark:text-green-400">${txn.total?.toFixed(2)}</td>
                        <td className="py-3 px-2">
                          <span className="font-mono bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 px-2 py-1 rounded">
                            {txn.mpaisa_ref_code || 'N/A'}
                          </span>
                        </td>
                        <td className="py-3 px-2 text-muted-foreground">{txn.customer_name || 'Walk-in'}</td>
                        <td className="py-3 px-2 text-muted-foreground text-xs">{txn.items?.join(', ')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* All Paid Orders */}
          <div className="bg-card rounded-xl p-6 border border-border">
            <h2 className="font-heading text-lg text-foreground mb-4">All Paid Orders</h2>
            {data.all_paid_orders.length === 0 ? (
              <p className="text-muted-foreground text-center py-8">No paid orders for this day</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left py-3 px-2 text-muted-foreground font-medium">Order #</th>
                      <th className="text-left py-3 px-2 text-muted-foreground font-medium">Time</th>
                      <th className="text-left py-3 px-2 text-muted-foreground font-medium">Amount</th>
                      <th className="text-left py-3 px-2 text-muted-foreground font-medium">Payment</th>
                      <th className="text-left py-3 px-2 text-muted-foreground font-medium">Ref Code</th>
                      <th className="text-left py-3 px-2 text-muted-foreground font-medium">Customer</th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.all_paid_orders.map((order, idx) => (
                      <tr key={idx} className="border-b border-border/50 hover:bg-muted/50">
                        <td className="py-3 px-2 font-medium">#{order.order_number}</td>
                        <td className="py-3 px-2 text-muted-foreground">{order.time || '-'}</td>
                        <td className="py-3 px-2 font-bold">${order.total?.toFixed(2)}</td>
                        <td className="py-3 px-2">
                          <span className="flex items-center gap-1">
                            {getPaymentIcon(order.payment_method)}
                            <span className="text-xs">{getPaymentLabel(order.payment_method)}</span>
                          </span>
                        </td>
                        <td className="py-3 px-2">
                          {order.mpaisa_ref_code ? (
                            <span className="font-mono bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-200 px-2 py-1 rounded text-xs">
                              {order.mpaisa_ref_code}
                            </span>
                          ) : '-'}
                        </td>
                        <td className="py-3 px-2 text-muted-foreground">{order.customer_name || 'Walk-in'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
